import { useState, useEffect } from 'react'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import styles from './add-note-input.module.css'
import Button from '@mui/material/Button'
import { NoteType } from '../../../../../../../app/types/order-notes'
import { IOrderNote } from '../../../../../../../app/entities/OrderNote'

interface AddNoteInputProps {
  onSubmit: (type: NoteType, content: string) => Promise<void>
  onEditSubmit?: (noteObjectId: string, content: string) => Promise<void>
  onCancelEdit?: () => void
  editingNote?: IOrderNote | null
  isReadOnly?: boolean
  isSubmitting?: boolean
}

export default function AddNoteInput({
  onSubmit,
  onEditSubmit,
  onCancelEdit,
  editingNote = null,
  isReadOnly = false,
  isSubmitting = false,
}: AddNoteInputProps) {
  const [noteType, setNoteType] = useState<NoteType | null>(null)
  const [content, setContent] = useState('')

  const isEditing = Boolean(editingNote)

  useEffect(() => {
    if (editingNote) {
      // Strip the "(Edited by ...)" prefix if present so user edits the actual content
      const existingContent = editingNote.content.replace(/^\(Edited(?: by [^)]+)?\)\s*/, '')
      setContent(existingContent)
      setNoteType(editingNote.type as NoteType)
    }
  }, [editingNote])

  async function handleSubmit() {
    if (!content.trim() || isSubmitting || isReadOnly) return

    if (isEditing && editingNote && onEditSubmit) {
      await onEditSubmit(editingNote.objectId, content.trim())
      setContent('')
      setNoteType(null)
    } else if (noteType) {
      await onSubmit(noteType, content.trim())
      setContent('')
      setNoteType(null)
    }
  }

  function handleCancel() {
    setContent('')
    setNoteType(null)
    onCancelEdit?.()
  }

  const isDisabled = isReadOnly || isSubmitting || !content.trim() || (!isEditing && !noteType)

  return (
    <div className={styles.container}>
      <textarea
        className={styles.textArea}
        placeholder={isEditing ? 'Edit note...' : 'Add a note...'}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        disabled={isReadOnly}
        rows={2}
      />
      <div className={styles.bottomRow}>
        <TextField
          select
          size='small'
          value={noteType || ''}
          label='Type'
          onChange={(e) => setNoteType(e.target.value as NoteType)}
          disabled={isReadOnly || isEditing}
          className={styles.typeSelect}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: { maxHeight: 200 },
              },
            },
          }}
        >
          <MenuItem value='technician_note'>Technician Note</MenuItem>
          <MenuItem value='activity_log'>Activity Log</MenuItem>
        </TextField>
        {isEditing && (
          <Button
            onClick={handleCancel}
            variant='outlined'
            style={{ textTransform: 'none' }}
            sx={{
              color: 'var(--text-secondary)',
              borderColor: 'var(--filter-border)',
              backgroundColor: 'var(--bg-card)',
              flex: 1,
              fontWeight: 600,
              fontSize: '14px',
            }}
            className={styles.addButton}
          >
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          variant='outlined'
          style={{ textTransform: 'none' }}
          sx={{
            color: isDisabled ? 'var(--text-secondary)' : 'var(--brand-primary)',
            borderColor: isDisabled ? 'var(--filter-border)' : 'var(--brand-primary)',
            backgroundColor: isDisabled ? 'var(--bg-disabled)' : 'var(--bg-card)',
            flex: 1,
            fontWeight: 600,
            fontSize: '14px',
          }}
          className={styles.addButton}
          disabled={isDisabled}
        >
          {isEditing ? 'Save' : 'Add'}
        </Button>
      </div>
    </div>
  )
}
